import { X, Plus, Minus, Trash2, Loader2, Package, Tag, ChevronLeft, BadgePercent, CheckCircle2, Gift, Truck, Store as StoreIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useCart } from "../context/CartContext";
import ProductImage from "./ProductImage";
import { api } from "../lib/api";
import { SHIPPING_FEE, SHIPPING_LABEL } from "../lib/pricing";

const PENDING_VOUCHER_KEY = "jdm_pending_voucher_v1";
const emptyCustomer = { name: "", email: "", phone: "", address: "", notes: "" };
const NOTES_MAX = 400;

// Next 14 pickup days (skip Mondays — store closed).
const buildPickupDays = () => {
  const out = [];
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const fmt = new Intl.DateTimeFormat("en-NZ", { weekday: "long", day: "numeric", month: "short" });
  let cursor = new Date(now);
  cursor.setDate(cursor.getDate() + 1); // start from tomorrow (min prep time)
  while (out.length < 10) {
    if (cursor.getDay() !== 1) {
      const iso = cursor.toISOString().slice(0, 10);
      out.push({ iso, label: fmt.format(cursor) });
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

const CartDrawer = () => {
  const { items, isOpen, closeCart, removeItem, updateQuantity, subtotal } = useCart();
  const [step, setStep] = useState("cart"); // "cart" | "details"
  const [customer, setCustomer] = useState(emptyCustomer);
  const [fulfillment, setFulfillment] = useState("ship"); // "ship" | "pickup"
  const [pickupDay, setPickupDay] = useState("");
  const pickupDays = useMemo(buildPickupDays, []);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Voucher state
  const [voucherInput, setVoucherInput] = useState("");
  const [voucher, setVoucher] = useState(null); // { code, type, discount_amount, free_shipping }
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherError, setVoucherError] = useState("");

  // Pending voucher nudge (set by NewsletterPopup once a visitor subscribes)
  const [pendingVoucher, setPendingVoucher] = useState(null); // { code }

  // Read the pending voucher from localStorage on mount + listen for live updates
  useEffect(() => {
    const read = () => {
      try {
        const raw = localStorage.getItem(PENDING_VOUCHER_KEY);
        if (!raw) { setPendingVoucher(null); return; }
        const parsed = JSON.parse(raw);
        if (parsed?.code) setPendingVoucher({ code: parsed.code });
        else setPendingVoucher(null);
      } catch {
        setPendingVoucher(null);
      }
    };
    read();
    window.addEventListener("jdm:pending-voucher-updated", read);
    return () => window.removeEventListener("jdm:pending-voucher-updated", read);
  }, []);

  const clearPendingVoucher = () => {
    try { localStorage.removeItem(PENDING_VOUCHER_KEY); } catch { /* ignore */ }
    setPendingVoucher(null);
  };

  const discountAmount = voucher?.discount_amount || 0;
  const freeShipping = !!voucher?.free_shipping;
  const isPickup = fulfillment === "pickup";
  const baseShipping = items.length > 0 && !isPickup ? SHIPPING_FEE : 0;
  const shipping = freeShipping || isPickup ? 0 : baseShipping;
  const total = Math.max(0, subtotal - discountAmount + shipping);

  const applyVoucher = async (codeOverride) => {
    const code = (typeof codeOverride === "string" ? codeOverride : voucherInput).trim();
    if (!code) return;
    setVoucherLoading(true);
    setVoucherError("");
    try {
      const { data } = await api.post("/voucher/validate", { code, subtotal });
      if (data.valid) {
        setVoucher({
          code: data.code,
          type: data.type,
          discount_amount: data.discount_amount || 0,
          free_shipping: !!data.free_shipping,
        });
        setVoucherInput(data.code);
        setVoucherError("");
        // If the just-applied code matches the pending nudge, clear it.
        if (pendingVoucher && pendingVoucher.code === data.code) {
          clearPendingVoucher();
        }
      } else {
        setVoucher(null);
        setVoucherError(data.message || "Voucher code is not valid.");
      }
    } catch (e) {
      setVoucher(null);
      setVoucherError(e?.response?.data?.detail || "Couldn't validate that voucher.");
    } finally {
      setVoucherLoading(false);
    }
  };

  const removeVoucher = () => {
    setVoucher(null);
    setVoucherInput("");
    setVoucherError("");
  };

  useEffect(() => {
    if (!voucher) return;
    if (items.length === 0 || subtotal <= 0) {
      setVoucher(null);
      setVoucherInput("");
      setVoucherError("");
      return;
    }
    applyVoucher(voucher.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const goToDetails = () => {
    if (items.length === 0) return;
    setError("");
    setFieldErrors({});
    setStep("details");
  };

  const goBackToCart = () => {
    setError("");
    setFieldErrors({});
    setStep("cart");
  };

  const validate = () => {
    const errs = {};
    if (customer.name.trim().length < 1) errs.name = "Required";
    if (customer.name.trim().length > 120) errs.name = "Too long";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email.trim())) errs.email = "Enter a valid email";
    if (customer.phone.trim().length < 4) errs.phone = "Enter a valid phone";
    if (customer.phone.trim().length > 40) errs.phone = "Too long";
    if (customer.notes.length > NOTES_MAX) errs.notes = `Please keep notes under ${NOTES_MAX} characters`;
    if (isPickup) {
      if (!pickupDay) errs.pickupDay = "Choose a pickup day";
    } else {
      if (customer.address.trim().length < 4) errs.address = "Enter your delivery address";
      if (customer.address.trim().length > 500) errs.address = "Too long";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handlePay = async () => {
    if (!validate()) return;
    setLoading(true);
    setError("");
    try {
      const payload = {
        items: items.flatMap((i) => {
          if (i.pack) {
            return Array.from({ length: i.quantity }).map(() => ({
              quantity: 1,
              pack: {
                category: i.pack.category,
                size: i.pack.size,
                components: i.pack.components.map((c) => ({
                  product_id: c.product_id,
                  quantity: c.quantity,
                })),
              },
            }));
          }
          return [{ product_id: i.id, quantity: i.quantity }];
        }),
        origin_url: window.location.origin,
        customer_name: customer.name.trim(),
        customer_email: customer.email.trim(),
        customer_phone: customer.phone.trim(),
        delivery_address: isPickup ? null : customer.address.trim(),
        order_notes: customer.notes.trim() || null,
        voucher_code: voucher?.code || null,
        fulfillment_type: fulfillment,
        pickup_day: isPickup ? (pickupDays.find((d) => d.iso === pickupDay)?.label || pickupDay) : null,
      };
      const { data } = await api.post("/checkout/session", payload);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Checkout failed");
      setLoading(false);
    }
  };

  const inputClass = (key) =>
    `w-full bg-black/40 border ${
      fieldErrors[key] ? "border-[#ff1a8c]" : "border-white/15"
    } rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#00b4ff] transition`;

  return (
    <>
      <div
        data-testid="cart-overlay"
        onClick={closeCart}
        className={`fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[70] h-full w-full max-w-md bg-[#050508] border-l border-[#ff1a8c]/30 shadow-[0_0_60px_rgba(255,26,140,0.25)] transition-transform duration-300 flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-2">
            {step === "details" && (
              <button
                data-testid="checkout-back-button"
                onClick={goBackToCart}
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#00b4ff]"
                aria-label="Back to cart"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <h2 className="font-script text-3xl neon-gradient-text">
              {step === "cart" ? "Your Cart" : "Your Details"}
            </h2>
          </div>
          <button
            data-testid="cart-close-button"
            onClick={closeCart}
            className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-[#ff1a8c]"
            aria-label="Close cart"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {step === "cart" ? (
            items.length === 0 ? (
              <div data-testid="cart-empty-state" className="flex flex-col items-center justify-center h-full text-center text-gray-400">
                <p className="font-script text-2xl mb-2 text-white">No sweets yet</p>
                <p className="text-sm">Browse the shop and add something delicious.</p>
              </div>
            ) : (
              <ul className="flex flex-col gap-4">
                {items.map((item) => {
                  const isPack = !!item.pack;
                  return (
                    <li
                      key={item.id}
                      data-testid={`cart-item-${item.id}`}
                      className={`flex gap-4 p-3 rounded-2xl ${isPack ? "border border-[#ff1a8c]/40 bg-[#ff1a8c]/5" : "glass"}`}
                    >
                      {isPack ? (
                        <div className="w-20 h-20 rounded-xl bg-[#ff1a8c]/15 border border-[#ff1a8c]/40 flex items-center justify-center shrink-0">
                          <Package className="w-8 h-8 text-[#ff1a8c]" />
                        </div>
                      ) : (
                        <ProductImage
                          src={item.image}
                          alt={item.name}
                          size="md"
                          className="w-20 h-20 rounded-xl object-cover shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0 flex flex-col">
                        <p className="font-semibold text-white">{item.name}</p>
                        <p className="text-[#00b4ff] text-sm">${item.price.toFixed(2)}</p>
                        {isPack && (
                          <div className="mt-1 space-y-0.5">
                            {item.pack.components.map((c) => (
                              <p key={c.product_id} className="text-[11px] text-gray-400 truncate">
                                {c.quantity}× {c.name}
                              </p>
                            ))}
                            {item.pack.discountPercent > 0 && (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#00b4ff] mt-1">
                                <Tag className="w-2.5 h-2.5" /> {item.pack.discountPercent}% off applied
                              </span>
                            )}
                          </div>
                        )}
                        <div className="mt-auto pt-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <button
                              data-testid={`cart-decrement-${item.id}`}
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center hover:border-[#00b4ff]"
                              aria-label="Decrease"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span data-testid={`cart-quantity-${item.id}`} className="text-sm w-6 text-center">{item.quantity}</span>
                            <button
                              data-testid={`cart-increment-${item.id}`}
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center hover:border-[#ff1a8c]"
                              aria-label="Increase"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <button
                            data-testid={`cart-remove-${item.id}`}
                            onClick={() => removeItem(item.id)}
                            className="text-gray-400 hover:text-[#ff1a8c]"
                            aria-label="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )
          ) : (
            <form
              data-testid="checkout-details-form"
              onSubmit={(e) => {
                e.preventDefault();
                handlePay();
              }}
              className="flex flex-col gap-4"
            >
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-1.5 block">Full name</label>
                <input
                  data-testid="checkout-name-input"
                  type="text"
                  value={customer.name}
                  onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                  placeholder="Jane Doe"
                  className={inputClass("name")}
                  autoComplete="name"
                />
                {fieldErrors.name && <p className="text-xs text-[#ff1a8c] mt-1">{fieldErrors.name}</p>}
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-1.5 block">Email</label>
                <input
                  data-testid="checkout-email-input"
                  type="email"
                  value={customer.email}
                  onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                  placeholder="you@example.com"
                  className={inputClass("email")}
                  autoComplete="email"
                />
                {fieldErrors.email && <p className="text-xs text-[#ff1a8c] mt-1">{fieldErrors.email}</p>}
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-1.5 block">Phone</label>
                <input
                  data-testid="checkout-phone-input"
                  type="tel"
                  value={customer.phone}
                  onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                  placeholder="021 234 5678"
                  className={inputClass("phone")}
                  autoComplete="tel"
                />
                {fieldErrors.phone && <p className="text-xs text-[#ff1a8c] mt-1">{fieldErrors.phone}</p>}
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-1.5">Fulfillment</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="fulfillment-ship-button"
                    onClick={() => setFulfillment("ship")}
                    className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl border transition-all text-center ${
                      !isPickup
                        ? "bg-[#ff1a8c] border-[#ff1a8c] text-white shadow-[0_0_16px_rgba(255,26,140,0.5)]"
                        : "border-white/15 text-gray-200 hover:border-[#00b4ff]"
                    }`}
                  >
                    <Truck className="w-4 h-4" />
                    <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">Ship NZ-wide</span>
                    <span className="text-[9px] uppercase tracking-[0.14em] opacity-80">${SHIPPING_FEE.toFixed(2)} flat</span>
                  </button>
                  <button
                    type="button"
                    data-testid="fulfillment-pickup-button"
                    onClick={() => setFulfillment("pickup")}
                    className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl border transition-all text-center ${
                      isPickup
                        ? "bg-[#00b4ff] border-[#00b4ff] text-black shadow-[0_0_16px_rgba(0,180,255,0.5)]"
                        : "border-white/15 text-gray-200 hover:border-[#ff1a8c]"
                    }`}
                  >
                    <StoreIcon className="w-4 h-4" />
                    <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">Click & Collect</span>
                    <span className="text-[9px] uppercase tracking-[0.14em] opacity-80">Free · In-store</span>
                  </button>
                </div>
              </div>

              {isPickup ? (
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-1.5 block">Pickup day</label>
                  <select
                    data-testid="checkout-pickup-day-select"
                    value={pickupDay}
                    onChange={(e) => setPickupDay(e.target.value)}
                    className={`w-full bg-black/40 border ${
                      fieldErrors.pickupDay ? "border-[#ff1a8c]" : "border-white/15"
                    } rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#00b4ff] transition`}
                  >
                    <option value="">Choose a day…</option>
                    {pickupDays.map((d) => (
                      <option key={d.iso} value={d.iso}>{d.label}</option>
                    ))}
                  </select>
                  {fieldErrors.pickupDay && <p className="text-xs text-[#ff1a8c] mt-1">{fieldErrors.pickupDay}</p>}
                  <p className="text-[11px] text-gray-500 tracking-[0.05em] mt-2">
                    Pickup from 113 Cambridge Road, Hillcrest, Hamilton · Open Tue–Sun · 12pm–10pm.
                  </p>
                </div>
              ) : (
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-1.5 block">Delivery address</label>
                  <textarea
                    data-testid="checkout-address-input"
                    value={customer.address}
                    onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
                    placeholder="Street, suburb, city, postcode"
                    rows={3}
                    className={inputClass("address") + " resize-none"}
                    autoComplete="street-address"
                  />
                  {fieldErrors.address && <p className="text-xs text-[#ff1a8c] mt-1">{fieldErrors.address}</p>}
                  <p className="text-[11px] text-gray-500 tracking-[0.05em] mt-2">
                    NZ-wide shipping flat rate of ${SHIPPING_FEE.toFixed(2)}. We&apos;ll send order updates to your email.
                  </p>
                </div>
              )}

              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-1.5 block flex items-center justify-between">
                  <span>Order notes <span className="normal-case tracking-normal text-gray-500 text-[10px]">(optional)</span></span>
                  <span className={`normal-case tracking-normal text-[10px] ${customer.notes.length > NOTES_MAX ? "text-[#ff1a8c]" : "text-gray-500"}`}>
                    {customer.notes.length}/{NOTES_MAX}
                  </span>
                </label>
                <textarea
                  data-testid="checkout-notes-input"
                  value={customer.notes}
                  onChange={(e) => setCustomer({ ...customer, notes: e.target.value.slice(0, NOTES_MAX + 20) })}
                  placeholder="Add a personal message, allergy info, or a special request (e.g. 'Happy Birthday Mia!')"
                  rows={3}
                  maxLength={NOTES_MAX + 20}
                  className={inputClass("notes") + " resize-none"}
                />
                {fieldErrors.notes && <p className="text-xs text-[#ff1a8c] mt-1">{fieldErrors.notes}</p>}
              </div>
            </form>
          )}
        </div>

        <div className="p-6 border-t border-white/10 space-y-3">
          {/* Voucher code */}
          {items.length > 0 && (
            <div data-testid="voucher-block" className="pb-2">
              {/* Pending-code nudge: shows if visitor subscribed but hasn't applied their code */}
              {pendingVoucher && !voucher && (
                <div
                  data-testid="pending-voucher-nudge"
                  className="mb-2 flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border border-[#ff1a8c]/50 bg-gradient-to-r from-[#ff1a8c]/15 to-[#00b4ff]/10"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Gift className="w-4 h-4 text-[#ff1a8c] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#ff1a8c] font-bold">You have a code waiting</p>
                      <p className="text-[11px] text-gray-300 truncate">
                        <span className="sm:hidden">Apply <span className="font-semibold text-white">{pendingVoucher.code}</span></span>
                        <span className="hidden sm:inline">Apply <span className="font-semibold text-white">{pendingVoucher.code}</span> for 10% off your online order.</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      data-testid="pending-voucher-apply-button"
                      onClick={() => applyVoucher(pendingVoucher.code)}
                      disabled={voucherLoading}
                      className="px-3 py-1.5 rounded-lg bg-[#ff1a8c] text-white text-[10px] uppercase tracking-[0.18em] font-bold hover:shadow-[0_0_18px_rgba(255,26,140,0.55)] disabled:opacity-50 transition"
                    >
                      Apply
                    </button>
                    <button
                      data-testid="pending-voucher-dismiss-button"
                      onClick={clearPendingVoucher}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-white"
                      aria-label="Dismiss reminder"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
              {!voucher ? (
                <div className="flex gap-2">
                  <input
                    data-testid="voucher-input"
                    type="text"
                    value={voucherInput}
                    onChange={(e) => setVoucherInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyVoucher(); } }}
                    placeholder="VOUCHER CODE"
                    className="flex-1 bg-black/40 border border-white/15 rounded-xl px-4 py-2.5 text-xs uppercase tracking-[0.18em] text-white placeholder:text-gray-500 focus:outline-none focus:border-[#00b4ff] transition"
                    autoComplete="off"
                  />
                  <button
                    data-testid="voucher-apply-button"
                    onClick={() => applyVoucher()}
                    disabled={voucherLoading || !voucherInput.trim()}
                    className="px-4 py-2.5 rounded-xl border border-[#00b4ff]/60 text-[#00b4ff] text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-[#00b4ff]/10 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    {voucherLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply"}
                  </button>
                </div>
              ) : (
                <div
                  data-testid="voucher-applied-pill"
                  className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border border-[#00b4ff]/60 bg-[#00b4ff]/10"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="w-4 h-4 text-[#00b4ff] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#00b4ff] font-bold">{voucher.code}</p>
                      <p className="text-[11px] text-gray-300 truncate">
                        {voucher.free_shipping
                          ? "Free shipping applied"
                          : `${voucher.type === "percent" ? "" : ""}-$${voucher.discount_amount.toFixed(2)} off`}
                      </p>
                    </div>
                  </div>
                  <button
                    data-testid="voucher-remove-button"
                    onClick={removeVoucher}
                    className="text-gray-400 hover:text-[#ff1a8c]"
                    aria-label="Remove voucher"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {voucherError && (
                <p data-testid="voucher-error" className="text-[11px] text-[#ff1a8c] mt-2 flex items-center gap-1.5">
                  <BadgePercent className="w-3 h-3" />
                  {voucherError}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between text-sm text-gray-300">
            <span className="uppercase tracking-[0.2em] text-gray-400 text-xs">Subtotal</span>
            <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
          </div>
          {voucher && discountAmount > 0 && (
            <div data-testid="cart-discount-line" className="flex items-center justify-between text-sm text-[#00b4ff]">
              <span className="uppercase tracking-[0.2em] text-xs">
                Discount <span className="normal-case text-gray-500">· {voucher.code}</span>
              </span>
              <span data-testid="cart-discount">−${discountAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-sm text-gray-300">
            <span className="uppercase tracking-[0.2em] text-gray-400 text-xs">
              {isPickup ? "Fulfillment" : "Shipping"} <span className="normal-case text-gray-500">· {isPickup ? "In-store pickup" : "NZ-wide"}</span>
            </span>
            <span data-testid="cart-shipping">
              {items.length === 0 ? "—" : isPickup ? (
                <span className="text-[#00b4ff]">Free</span>
              ) : freeShipping ? (
                <span className="text-[#00b4ff]"><span className="line-through text-gray-500 mr-2">${SHIPPING_FEE.toFixed(2)}</span>Free</span>
              ) : `$${SHIPPING_FEE.toFixed(2)}`}
            </span>
          </div>
          <hr className="hr-neon" />
          <div className="flex items-center justify-between">
            <span className="uppercase tracking-[0.2em] text-gray-300 text-xs">Total</span>
            <span data-testid="cart-total" className="text-2xl font-bold neon-blue">
              ${total.toFixed(2)}
            </span>
          </div>
          {error && (
            <p data-testid="cart-error-message" className="text-sm text-[#ff1a8c]">{error}</p>
          )}
          {step === "cart" ? (
            <button
              data-testid="cart-checkout-button"
              onClick={goToDetails}
              disabled={items.length === 0}
              className="neon-btn w-full justify-center"
            >
              Continue to details
            </button>
          ) : (
            <button
              data-testid="checkout-pay-button"
              onClick={handlePay}
              disabled={loading}
              className="neon-btn w-full justify-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Windcave…
                </>
              ) : (
                `Pay $${total.toFixed(2)} securely`
              )}
            </button>
          )}
          <p className="text-[10px] text-gray-500 text-center tracking-[0.15em] uppercase">
            {SHIPPING_LABEL} · Secure payment via Windcave
          </p>
        </div>
      </aside>
    </>
  );
};

export default CartDrawer;
